<template>
  <b-card
    body
    class="rounded-[8px] !shadow-none"
  >
    <validation-observer
      ref="informasiGudangForm"
      #default
    >
      <b-form
        @submit.prevent="submitForm"
      >
        <div class="d-flex items-center justify-content-between mb-[16px]">
          <h2 class="text-black font-semibold text-[20px] mb-0 flex items-center gap-[8px]">
            Biaya Gudang <b-spinner
              v-if="isLoading"
              small
            />
          </h2>
          <div class="flex items-center gap-[8px]">
            <b-button
              variant="outline-primary"
              size="sm"
              :disabled="onEditMode || isLoading || isCostOnReview"
              @click="onEditMode = !onEditMode"
            >
              Edit
            </b-button>
            <b-button
              type="submit"
              :variant="onEditMode ? 'primary' : 'secondary'"
              size="sm"
              :disabled="!onEditMode || isPending"
              class="!flex items-center gap-[8px]"
            >
              <b-spinner
                v-if="isPending"
                small
              />
              Simpan
            </b-button>
          </div>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-3 gap-[16px] w-full">
          <ReusableInput
            v-model="tarifPenyimpanan"
            name="Tarif Penyimpanan"
            label="Tarif Penyimpanan"
            placeholder="Masukkan tarif penyimpanan"
            rules="required"
            :disabled="!onEditMode"
            currency
          >
            <template #input="{ value, disabled, onInput, onBlur, onFocus, dataId, type, placeholder }">
              <b-input-group>
                <b-form-input
                  :id="dataId"
                  :value="value"
                  :placeholder="placeholder"
                  :disabled="disabled"
                  :type="type"
                  class="!text-[#333333] border disabled:!bg-[#F4F4F4] !py-[10px]"
                  style="border-color: #E2E2E2!important;"
                  @input="onInput($event)"
                  @blur="onBlur($event)"
                  @focus="onFocus($event)"
                />
                <b-input-group-append>
                  <b-input-group-text
                    class="!border-[#E2E2E2]"
                    :class="{'!bg-[#F4F4F4]': !onEditMode}"
                  >
                    m³
                  </b-input-group-text>
                </b-input-group-append>
              </b-input-group>
            </template>
          </ReusableInput>
          <ReusableInput
            v-model="biayaJasaPacking"
            name="Biaya Jasa Packing"
            label="Biaya Jasa Packing"
            placeholder="Masukkan biaya jasa packing"
            rules="required"
            :disabled="!onEditMode"
            currency
          />
          <ReusableInput
            v-model="biayaPrintLabel"
            name="Biaya Print Label"
            label="Biaya Print Label"
            placeholder="Masukkan biaya print label"
            rules="required"
            :disabled="!onEditMode"
            currency
          />
          <ReusableInput
            v-if="eligibleForColdStorage"
            v-model="biayaColdStorage"
            name="Biaya Cold Storage"
            label="Biaya Cold Storage"
            placeholder="Masukkan biaya cold storage"
            rules="required"
            :disabled="!onEditMode"
            currency
          >
            <template #input="{ value, disabled, onInput, onBlur, onFocus, dataId, type, placeholder }">
              <b-input-group>
                <b-form-input
                  :id="dataId"
                  :value="value"
                  :placeholder="placeholder"
                  :disabled="disabled"
                  :type="type"
                  class="!text-[#333333] border disabled:!bg-[#F4F4F4] !py-[10px]"
                  style="border-color: #E2E2E2!important;"
                  @input="onInput($event)"
                  @blur="onBlur($event)"
                  @focus="onFocus($event)"
                />
                <b-input-group-append>
                  <b-input-group-text
                    class="!border-[#E2E2E2]"
                    :class="{'!bg-[#F4F4F4]': !onEditMode}"
                  >
                    m³
                  </b-input-group-text>
                </b-input-group-append>
              </b-input-group>
            </template>
          </ReusableInput>
        </div>
      </b-form>
    </validation-observer>

    <modal-general-action ref="mgac_kompack" />

    <modal-being-verified-by-admin ref="mbvba_kompack" />
  </b-card>
</template>

<script>
import { ValidationObserver } from 'vee-validate'
import { toastError } from '@/libs/toastification'
import ReusableInput from './Input.vue'
import ModalGeneralAction from './ModalGeneralAction.vue'
import ModalBeingVerifiedByAdmin from './ModalBeingVerifiedByAdmin.vue'

/**
 * CardBiayaGudang
 * @description CardBiayaGudang is a component
 * that displays, edit and delete the cost of the warehouse cost detail
 * @param {Number} warehouseId - The id of the warehouse
 * @returns {Object} CardBiayaGudang
 */
export default {
  name: 'CardBiayaGudang',
  components: {
    ValidationObserver,
    ReusableInput,
    ModalGeneralAction,
    ModalBeingVerifiedByAdmin,
  },
  props: {
    warehouseId: {
      type: Number,
      required: true,
    },
    facilities: {
      type: Array,
      required: false,
      default: () => [],
    },
  },
  data() {
    return {
      isLoading: true,
      isPending: false,
      onEditMode: false,
      isCostOnReview: false,
      tarifPenyimpanan: 0,
      biayaJasaPacking: 0,
      biayaPrintLabel: 0,
      biayaColdStorage: 0,
      rawTarifPenyimpanan: 0,
      rawBiayaJasaPacking: 0,
      rawBiayaPrintLabel: 0,
      rawBiayaColdStorage: 0,
    }
  },
  computed: {
    eligibleForColdStorage() {
      return this.facilities.some(facility => facility.name === 'Cold Storage')
    },
  },
  watch: {
    warehouseId: {
      handler(newVal) {
        if (newVal >= 0) {
          this.fetchWarehouseCost()
        }
      },
      immediate: true,
    },
  },
  methods: {
    /**
     * Validate the form, take user confirmation and submit the data.
     * The mutation will be running one by onebecause the backend endpoint not support batch mutation
     */
    submitForm() {
      this.isPending = true
      this.$refs.informasiGudangForm.validate().then(success => {
        if (success) {
          const tarifPenyimpananUpdated = this.rawTarifPenyimpanan !== this.tarifPenyimpanan
          const biayaJasaPackingUpdated = this.rawBiayaJasaPacking !== this.biayaJasaPacking
          const biayaPrintLabelUpdated = this.rawBiayaPrintLabel !== this.biayaPrintLabel
          const biayaColdStorageUpdated = this.rawBiayaColdStorage !== this.biayaColdStorage

          this.$refs.mgac_kompack.show({
            title: 'Konfirmasi perubahan data biaya gudang',
            message: 'Pastikan data yang kamu ubah sudah benar',
            confirmText: 'Simpan',
            onConfirm: async ({ hide: closeModal, setIsLoading }) => {
              setIsLoading(true)
              try {
                if (!tarifPenyimpananUpdated && !biayaJasaPackingUpdated && !biayaPrintLabelUpdated && !biayaColdStorageUpdated) {
                  this.onEditMode = false
                  closeModal()
                  return
                }
                const mutates = []
                if (tarifPenyimpananUpdated) {
                  mutates.push(this.fnMutateCost({
                    service: 1, // 1 = penyimpanan
                    price: Number(this.tarifPenyimpanan),
                    errorMessage: 'Gagal mengubah data biaya penyimpanan, silahkan refresh untuk coba lagi',
                  }))
                }
                if (biayaJasaPackingUpdated) {
                  mutates.push(this.fnMutateCost({
                    service: 2, // 2 = jasa packing
                    price: Number(this.biayaJasaPacking),
                    errorMessage: 'Gagal mengubah data biaya jasa packing, silahkan refresh untuk coba lagi',
                  }))
                }
                if (biayaPrintLabelUpdated) {
                  mutates.push(this.fnMutateCost({
                    service: 3, // 3 = print label
                    price: Number(this.biayaPrintLabel),
                    errorMessage: 'Gagal mengubah data biaya print label, silahkan refresh untuk coba lagi',
                  }))
                }
                if (biayaColdStorageUpdated) {
                  mutates.push(this.fnMutateCost({
                    service: 4, // 4 = cold storage
                    price: Number(this.biayaColdStorage),
                    errorMessage: 'Gagal mengubah data biaya cold storage, silahkan refresh untuk coba lagi',
                  }))
                }

                const result = await Promise.all(mutates)

                const isSuccess = result.every(x => x)

                if (isSuccess) {
                  closeModal()
                  this.$refs.mbvba_kompack.show()
                }
              } catch (error) {
                toastError('Gagal mengubah data biaya gudang, silahkan refresh untuk coba lagi')
              } finally {
                setIsLoading(false)
                this.onEditMode = false
              }
            },
          })
        }
      }).finally(() => {
        this.isPending = false
      })
    },
    /**
     * Fetch warehouse cost and map the data to the component
     */
    async fetchWarehouseCost() {
      try {
        const { data: result } = await this.$http_new.get(`/komship/api/v1/warehouse-costs/${this.warehouseId}`)
        const { data } = result
        const isCostOnReview = data.storage_cost_disabled || data.packing_service_cost_disabled || data.label_print_cost_disabled || data.cold_storage_cost_disabled
        if (isCostOnReview) {
          this.isCostOnReview = true
          this.onEditMode = false
        }
        this.tarifPenyimpanan = data.storage_cost
        this.biayaJasaPacking = data.packing_service_cost
        this.biayaPrintLabel = data.label_print_cost
        this.biayaColdStorage = data.cold_storage_cost
        this.rawTarifPenyimpanan = data.storage_cost
        this.rawBiayaJasaPacking = data.packing_service_cost
        this.rawBiayaPrintLabel = data.label_print_cost
        this.rawBiayaColdStorage = data.cold_storage_cost
      } catch (error) {
        toastError('Gagal mengambil data biaya gudang, silahkan refresh untuk coba lagi')
      } finally {
        this.isLoading = false
      }
    },
    /**
     * Mutate cost and submit the data. For submit data, the submission status will be constant of 1 (diajukan)
     * @param {Object} params - The parameters
     * @param {Number} params.service - The service: 1 = penyimpanan, 2 = jasa packing, 3 = print label
     * @param {Number} params.price - The price
     * @param {Number} params.submission_status - The submission status(would be constant of 1 (diajukan)): 1 = diajukan(default), 2 = disetujui, 3 = ditolak
     * @param {String} params.errorMessage - The error message
     * @returns {Promise<boolean>}
     */
    async fnMutateCost({ service, price, errorMessage = 'Gagal mengubah data biaya gudang, silahkan refresh untuk coba lagi' }) {
      try {
        await this.$http_new.post('/komship/api/v1/warehouse-cost-submissions', {
          service,
          price,
          submission_status: 1, // 1 = diajukan
        })
        return true
      } catch (error) {
        toastError(errorMessage)
        return false
      }
    },
  },
}
</script>
